/* Info tabs.
   Tab membership comes from block order: the first content block is tab 1, the
   second is tab 2, and so on. Nothing to configure per block. */

/* Splits "6,000mg" into prefix "", digits "6,000", suffix "mg" so only the
   number animates and any symbol stays put. */
function parseNumber(text) {
  const match = String(text).match(/^(\D*?)([\d.,]+)(.*)$/s);
  if (!match) return null;

  const raw = match[2];
  const grouped = raw.includes(',');
  const decimals = raw.includes('.') ? raw.split('.')[1].length : 0;
  const value = parseFloat(raw.replace(/,/g, ''));
  if (Number.isNaN(value)) return null;

  // "07%" keeps its leading zero all the way up.
  const pad = raw.replace(/,/g, '').split('.')[0].length;

  return { prefix: match[1], suffix: match[3], value, decimals, grouped, pad };
}

function format(value, parsed) {
  const fixed = value.toFixed(parsed.decimals);
  let [whole, fraction] = fixed.split('.');

  if (parsed.grouped) {
    whole = Number(whole).toLocaleString('en-US');
  } else if (parsed.pad > 1) {
    whole = whole.padStart(parsed.pad, '0');
  }

  return fraction ? whole + '.' + fraction : whole;
}

function countUp(el, duration) {
  if (el.dataset.counted === 'true') return;

  const parsed = parseNumber(el.textContent.trim());
  if (!parsed) {
    el.dataset.counted = 'true';
    return;
  }

  el.dataset.counted = 'true';

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const start = performance.now();

  function frame(now) {
    const progress = Math.min((now - start) / duration, 1);
    // ease-out cubic, so it decelerates into the final number
    const eased = 1 - Math.pow(1 - progress, 3);
    const current = parsed.value * eased;

    el.textContent = parsed.prefix + format(current, parsed) + parsed.suffix;

    if (progress < 1) requestAnimationFrame(frame);
  }

  el.textContent = parsed.prefix + format(0, parsed) + parsed.suffix;
  requestAnimationFrame(frame);
}

class InfoTabs extends HTMLElement {
  connectedCallback() {
    this.groups = Array.from(this.querySelectorAll('.itabs__group'));
    this.notes = Array.from(this.querySelectorAll('[data-itab-note]'));

    const allTabs = Array.from(this.querySelectorAll('[data-itab]'));

    // A tab button with no content block behind it is not shown.
    allTabs.forEach((tab, index) => {
      tab.hidden = index >= this.groups.length;
    });
    this.tabs = allTabs.filter((tab) => !tab.hidden);

    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.select(index));
    });

    this.addEventListener('keydown', (event) => this.onKeydown(event));

    this.select(0);
    this.watchForEntry();
  }

  /* Counting starts when the block scrolls into view, not on page load — a
     visitor who never reaches it would otherwise miss the whole animation. */
  watchForEntry() {
    const root = this.closest('.itabs');
    this.countEnabled = root ? root.dataset.countUp === 'true' : false;
    this.countDuration = root ? Number(root.dataset.countDuration) || 1400 : 1400;

    if (!this.countEnabled || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        this.seen = true;
        this.countVisible();
        observer.disconnect();
      });
    }, { threshold: 0.25 });

    observer.observe(this);
  }

  countVisible() {
    if (!this.countEnabled || !this.seen) return;
    const group = this.groups[this.current];
    if (!group) return;
    group.querySelectorAll('.itabs__stat').forEach((el) => {
      countUp(el, this.countDuration);
    });
  }

  select(index) {
    if (index < 0 || index >= this.tabs.length) return;
    this.current = index;

    this.tabs.forEach((tab, i) => {
      const active = i === index;
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.tabIndex = active ? 0 : -1;
    });

    this.groups.forEach((group, i) => {
      group.hidden = i !== index;
    });

    this.notes.forEach((note) => {
      note.hidden = Number(note.dataset.itabNote) !== index + 1;
    });

    // A tab opened later counts up the first time it is shown.
    this.countVisible();
  }

  onKeydown(event) {
    const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (!keys.includes(event.key)) return;
    if (this.current === undefined) return;

    let next = this.current;
    if (event.key === 'ArrowLeft') next = this.current - 1;
    if (event.key === 'ArrowRight') next = this.current + 1;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = this.tabs.length - 1;

    if (next < 0) next = this.tabs.length - 1;
    if (next > this.tabs.length - 1) next = 0;

    event.preventDefault();
    this.select(next);
    this.tabs[next].focus();
  }
}

if (!customElements.get('info-tabs')) {
  customElements.define('info-tabs', InfoTabs);
}

/* Theme editor: selecting a content block opens its tab, so what you are
   editing never hides behind an inactive tab. */
document.addEventListener('shopify:block:select', (event) => {
  const target = event.target;
  if (!target || !target.closest) return;

  const group = target.closest('.itabs__group');
  if (!group) return;

  const host = group.closest('info-tabs');
  if (!host || typeof host.select !== 'function') return;

  host.select(host.groups.indexOf(group));
});